import { Route, Routes, useLocation } from 'react-router-dom';
import { Login } from '../components/pages/Login';
import { NotFound } from '../components/pages/NotFound';
import { Reservation } from '../components/pages/Reservation';
import { ReservationComplete } from '../components/pages/ReservationComplete';
import { ReservationConfirmation } from '../components/pages/ReservationConfirmation';
import { ReservationDetail } from '../components/pages/ReservationDetail';
import { ReservationHistory } from '../components/pages/ReservationHistory';
import { Top } from '../components/pages/Top';
import { ROUTE_PATH } from '../constants/routes';
import { ALLOWED_ROLES } from '../constants/users';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { Header } from '@/components/elements/Header';
import { ChangePassword } from '@/components/pages/ChangePassword';
import { Faq } from '@/components/pages/Faq';
import { HasBitkeyAccount } from '@/components/pages/HasBitkeyAccount';
import { MyPage } from '@/components/pages/MyPage';
import { PasswordReset } from '@/components/pages/PasswordReset';
import { PasswordResetComplete } from '@/components/pages/PasswordResetComplete';
import { PasswordResetSent } from '@/components/pages/PasswordResetSent';
import { PasswordSettingConfirm } from '@/components/pages/PasswordSettingConfirm';
import { RenewalNotice } from '@/components/pages/RenewalNotice';
import { ShareDevice } from '@/components/pages/ShareDevice';
import { ShareDeviceComplete } from '@/components/pages/ShareDeviceComplete';
import { ShareDeviceMe } from '@/components/pages/ShareDeviceMe';
import { ShareDeviceTimeSelection } from '@/components/pages/ShareDeviceTimeSelection';
import { ShareDeviceUserInfo } from '@/components/pages/ShareDeviceUserInfo';
import { SignUp } from '@/components/pages/SignUp';
import { SignUpComplete } from '@/components/pages/SignUpComplete';
import { SignUpConfirm } from '@/components/pages/SignUpConfirmation';
import { StaffLogin } from '@/components/pages/StaffLogin';
import { StaffOnly } from '@/components/pages/StaffOnly';
import { StaffOnlyResults } from '@/components/pages/StaffOnlyResults';
import { SuiteLoungeEntrypoint } from '@/components/pages/SuiteLoungeEntrypoint';
import { isStaffPage } from '@/utils/isStaffPage';
import { parseSubdomain } from '@/utils/subdomainParser';

export const Routing = () => {
  const location = useLocation();

  // サブドメインの有無で共有端末かどうかを判定
  const isShareDevicePage = parseSubdomain(window.location.hostname) !== null;

  // スタッフページかどうかを判定
  const isStaffPageValue = isStaffPage(location.pathname);

  // 共有端末ページとスタッフページ以外でHeaderを表示
  const showHeader = !isShareDevicePage && !isStaffPageValue;

  return (
    <>
      {showHeader && <Header />}

      <Routes>
        <Route path={ROUTE_PATH.LOGIN} element={<Login />} />
        <Route
          path={ROUTE_PATH.SUITE_LOUNGE_LOGIN}
          element={<SuiteLoungeEntrypoint />}
        />
        <Route path={ROUTE_PATH.RENEWAL_NOTICE} element={<RenewalNotice />} />
        <Route
          path={ROUTE_PATH.PASSWORD_SETTING_CONFIRM}
          element={<PasswordSettingConfirm />}
        />
        <Route
          path={ROUTE_PATH.HAS_BITKEY_ACCOUNT}
          element={<HasBitkeyAccount />}
        />
        <Route path={ROUTE_PATH.SIGN_UP} element={<SignUp />} />
        <Route
          path={ROUTE_PATH.SIGN_UP_COMPLETE}
          element={<SignUpComplete />}
        />
        <Route path={ROUTE_PATH.SIGN_UP_CONFIRM} element={<SignUpConfirm />} />
        <Route path={ROUTE_PATH.PASSWORD_RESET} element={<PasswordReset />} />
        <Route
          path={ROUTE_PATH.PASSWORD_RESET_SENT}
          element={<PasswordResetSent />}
        />
        <Route path={ROUTE_PATH.CHANGE_PASSWORD} element={<ChangePassword />} />
        <Route
          path={ROUTE_PATH.PASSWORD_RESET_COMPLETE}
          element={<PasswordResetComplete />}
        />
        {/* 認証が必要なページ */}
        <Route
          path={ROUTE_PATH.TOP}
          element={
            <ProtectedRoute>
              <Top />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.RESERVATION}
          element={
            <ProtectedRoute>
              <Reservation />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.RESERVATION_CONFIRM}
          element={
            <ProtectedRoute>
              <ReservationConfirmation />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.RESERVATION_COMPLETE}
          element={
            <ProtectedRoute>
              <ReservationComplete />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.RESERVATION_HISTORY}
          element={
            <ProtectedRoute>
              <ReservationHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.RESERVATION_DETAIL}
          element={
            <ProtectedRoute>
              <ReservationDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.MY_PAGE}
          element={
            <ProtectedRoute>
              <MyPage />
            </ProtectedRoute>
          }
        />
        <Route path={ROUTE_PATH.FAQ} element={<Faq />} />
        <Route
          path={ROUTE_PATH.SHARE_DEVICE}
          element={
            <ProtectedRoute allowedRoles={ALLOWED_ROLES.SHARED_DEVICE_PAGES}>
              <ShareDevice />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.SHARE_DEVICE_TIME_SELECTION}
          element={
            <ProtectedRoute allowedRoles={ALLOWED_ROLES.SHARED_DEVICE_PAGES}>
              <ShareDeviceTimeSelection />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.SHARE_DEVICE_USER_INFO}
          element={
            <ProtectedRoute allowedRoles={ALLOWED_ROLES.SHARED_DEVICE_PAGES}>
              <ShareDeviceUserInfo />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.SHARE_DEVICE_COMPLETE}
          element={
            <ProtectedRoute allowedRoles={ALLOWED_ROLES.SHARED_DEVICE_PAGES}>
              <ShareDeviceComplete />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.SHARE_DEVICE_ME}
          element={
            <ProtectedRoute allowedRoles={['shared_device']}>
              <ShareDeviceMe />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE_PATH.STAFF_ONLY}
          element={
            <ProtectedRoute allowedRoles={['staff']}>
              <StaffOnly />
            </ProtectedRoute>
          }
        />
        <Route path={ROUTE_PATH.STAFF_LOGIN} element={<StaffLogin />} />
        <Route
          path={ROUTE_PATH.STAFF_ONLY_RESULTS}
          element={
            <ProtectedRoute allowedRoles={['staff']}>
              <StaffOnlyResults />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};
